import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import { DailyDataPoint } from '../utils/goalData';
interface PacingChartProps {
  dailyData: DailyDataPoint[];
  daysPassed: number;
  totalDays: number;
}
type MetricFilter = 'both' | 'softPull' | 'submitted';
function CustomTooltip({
  active,
  payload,
  label
}: any) {
  if (!active || !payload || payload.length === 0) return null;
  return <div className="bg-slate-800 rounded-xl shadow-2xl border border-slate-700 p-4 min-w-[180px]" style={{
    boxShadow: '0 0 20px rgba(0,0,0,0.5)'
  }} data-id="element-121">
      <p className="text-sm font-semibold text-slate-200 mb-2" data-id="element-122">{label}</p>
      <div className="space-y-1.5" data-id="element-123">
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center justify-between gap-4" data-id="element-124">
            <div className="flex items-center gap-2" data-id="element-125">
              <div className="w-2.5 h-2.5 rounded-full" style={{
            backgroundColor: entry.color,
            boxShadow: `0 0 6px ${entry.color}`
          }} data-id="element-126" />
              <span className="text-xs text-slate-400" data-id="element-127">{entry.name}</span>
            </div>
            <span className="text-xs font-semibold text-white" data-id="element-128">
              {entry.value}
            </span>
          </div>)}
      </div>
    </div>;
}
export function PacingChart({
  dailyData,
  daysPassed,
  totalDays
}: PacingChartProps) {
  const [metricFilter, setMetricFilter] = useState<MetricFilter>('both');
  const chartData = dailyData.map(d => ({
    ...d,
    softPullActual: d.day <= daysPassed ? d.softPullActual : undefined,
    submittedActual: d.day <= daysPassed ? d.submittedActual : undefined
  }));
  const showSoftPull = metricFilter === 'both' || metricFilter === 'softPull';
  const showSubmitted = metricFilter === 'both' || metricFilter === 'submitted';
  const filterButtons: {
    label: string;
    value: MetricFilter;
  }[] = [{
    label: 'Both',
    value: 'both'
  }, {
    label: 'Soft Pull',
    value: 'softPull'
  }, {
    label: 'Submitted',
    value: 'submitted'
  }];
  return <section className="bg-slate-800/80 rounded-2xl border border-slate-700/50 p-6" style={{
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
  }} aria-label="Pacing chart" data-id="element-129">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-130">
        <div data-id="element-131">
          <h3 className="text-lg font-semibold text-white" data-id="element-132">Pacing Overview</h3>
          <p className="text-sm text-slate-400 mt-0.5" data-id="element-133">
            Actual progress vs. ideal pace
          </p>
        </div>

        <div className="flex items-center gap-1 bg-slate-900/80 rounded-lg p-1 border border-slate-700/50" role="radiogroup" aria-label="Metric filter" data-id="element-134">
          {filterButtons.map(btn => <button key={btn.value} onClick={() => setMetricFilter(btn.value)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${metricFilter === btn.value ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`} role="radio" aria-checked={metricFilter === btn.value} data-id="element-135">
              {btn.label}
            </button>)}
        </div>
      </div>

      <div className="h-[320px]" data-id="element-136">
        <ResponsiveContainer width="100%" height="100%" data-id="element-137">
          <AreaChart data={chartData} margin={{
          top: 8,
          right: 8,
          left: -12,
          bottom: 0
        }} data-id="element-138">
            <defs data-id="element-139">
              <linearGradient id="softPullGradientDark" x1="0" y1="0" x2="0" y2="1" data-id="element-140">
                <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3} data-id="element-141" />
                <stop offset="95%" stopColor="#818cf8" stopOpacity={0} data-id="element-142" />
              </linearGradient>
              <linearGradient id="submittedGradientDark" x1="0" y1="0" x2="0" y2="1" data-id="element-143">
                <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.3} data-id="element-144" />
                <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} data-id="element-145" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} data-id="element-146" />
            <XAxis dataKey="label" tick={{
            fontSize: 12,
            fill: '#64748b'
          }} axisLine={{
            stroke: '#475569'
          }} tickLine={false} data-id="element-147" />
            <YAxis tick={{
            fontSize: 12,
            fill: '#64748b'
          }} axisLine={false} tickLine={false} data-id="element-148" />
            <Tooltip content={<CustomTooltip data-id="element-150" />} data-id="element-149" />
            <Legend verticalAlign="top" height={36} iconType="circle" iconSize={8} wrapperStyle={{
            fontSize: '12px',
            paddingBottom: '8px',
            color: '#94a3b8'
          }} data-id="element-151" />

            {daysPassed < totalDays && <ReferenceLine x={dailyData[daysPassed - 1]?.label} stroke="#64748b" strokeDasharray="4 4" label={{
            value: 'Today',
            position: 'top',
            fontSize: 11,
            fill: '#94a3b8'
          }} data-id="element-152" />}

            {/* Ideal pace lines (dashed) */}
            {showSoftPull && <Area type="monotone" dataKey="softPullIdeal" name="SP Ideal" stroke="#6366f1" strokeWidth={2} strokeDasharray="6 4" fill="none" dot={false} connectNulls data-id="element-153" />}
            {showSubmitted && <Area type="monotone" dataKey="submittedIdeal" name="Sub Ideal" stroke="#0891b2" strokeWidth={2} strokeDasharray="6 4" fill="none" dot={false} connectNulls data-id="element-154" />}

            {/* Actual progress lines (solid with gradient fill) */}
            {showSoftPull && <Area type="monotone" dataKey="softPullActual" name="Soft Pull" stroke="#818cf8" strokeWidth={2.5} fill="url(#softPullGradientDark)" dot={false} activeDot={{
            r: 5,
            fill: '#818cf8',
            stroke: '#1e293b',
            strokeWidth: 2
          }} connectNulls data-id="element-155" />}
            {showSubmitted && <Area type="monotone" dataKey="submittedActual" name="Submitted" stroke="#22d3ee" strokeWidth={2.5} fill="url(#submittedGradientDark)" dot={false} activeDot={{
            r: 5,
            fill: '#22d3ee',
            stroke: '#1e293b',
            strokeWidth: 2
          }} connectNulls data-id="element-156" />}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Chart legend note */}
      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-700/50" data-id="element-157">
        <div className="flex items-center gap-2" data-id="element-158">
          <div className="w-6 h-0.5" style={{
          backgroundImage: 'repeating-linear-gradient(90deg, #6366f1 0, #6366f1 4px, transparent 4px, transparent 8px)'
        }} data-id="element-159" />
          <span className="text-xs text-slate-500" data-id="element-160">Ideal pace</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-161">
          <div className="w-6 h-0.5 bg-indigo-400 rounded" data-id="element-162" />
          <span className="text-xs text-slate-500" data-id="element-163">Actual progress</span>
        </div>
      </div>
    </section>;
}